import { useState, useEffect } from 'react';
import { Plus, ChevronDown, Check, FileText, RefreshCw, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { DropdownMenu } from '../ui/DropdownMenu';
import { apiClient } from '../../lib/api-client';
import { WorkflowConfigEditor } from './WorkflowConfigEditor';

interface WorkflowParameter {
  name: string;
  type: string;
  required: boolean;
  default?: any;
  description?: string;
  choices?: string[];
  dynamicSource?: string;
  dynamicPath?: string;
  dependsOn?: string[];
  label?: string;
}

interface ScheduleEditorProps {
  isOpen: boolean;
  onClose: () => void;
  workflow: {
    name: string;
    displayName: string;
    description: string;
    parameters: WorkflowParameter[];
    annotations?: Record<string, string>;
    configType?: string;
  };
  onSubmit: (schedule: { workflowName: string; parameters: Record<string, any>; schedule: string }) => void;
  isSubmitting: boolean;
}

type DefaultConfigStatus = 'idle' | 'loading' | 'loaded' | 'error';

const PRESETS = {
  hourly:  { label: 'Every Hour',              cron: '0 * * * *'   },
  daily:   { label: 'Daily (Midnight)',         cron: '0 0 * * *'   },
  weekly:  { label: 'Weekly (Sunday Midnight)', cron: '0 0 * * 0'   },
  monthly: { label: 'Monthly (1st Day)',        cron: '0 0 1 * *'   },
} as const;

type PresetKey = keyof typeof PRESETS;

export function ScheduleEditor({ isOpen, onClose, workflow, onSubmit, isSubmitting }: ScheduleEditorProps) {
  const isConfigurable = workflow.annotations?.['portal.maxiron.cloud/configurable'] === 'true';
  const configType = workflow.configType ?? workflow.annotations?.['portal.maxiron.cloud/config-type'];
  const isTenantAware = isConfigurable && configType === 'tenant-aware';
  const requiresDocker = workflow.annotations?.['portal.maxiron.cloud/requires-docker'] === 'true';

  // Schedule state
  const [usePreset, setUsePreset] = useState(true);
  const [preset, setPreset] = useState<PresetKey>('daily');
  const [cronExpression, setCronExpression] = useState('0 0 * * *');

  // Parameters state
  const [parameters, setParameters] = useState<Record<string, any>>(() => {
    const defaults: Record<string, any> = {};
    workflow.parameters.forEach((p) => { if (p.default !== undefined) defaults[p.name] = p.default; });
    return defaults;
  });

  // Tenant list
  const [tenants, setTenants] = useState<Array<{ code: string; name: string }>>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Dynamic options for provider/environment/etc.
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, string[]>>({});
  const [loadingDynamicOptions, setLoadingDynamicOptions] = useState<Record<string, boolean>>({});

  // YAML config state
  const [yamlContent, setYamlContent] = useState('');
  const [isYamlValid, setIsYamlValid] = useState(true);
  const [defaultConfigStatus, setDefaultConfigStatus] = useState<DefaultConfigStatus>('idle');
  const [defaultConfigLoading, setDefaultConfigLoading] = useState(false);
  const [defaultConfigError, setDefaultConfigError] = useState<string | null>(null);
  const [loadedForParams, setLoadedForParams] = useState('');

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isOpen) loadTenants();
  }, [isOpen]);

  useEffect(() => {
    const defaults: Record<string, any> = {};
    workflow.parameters.forEach((p) => { if (p.default !== undefined) defaults[p.name] = p.default; });
    setParameters(defaults);
    setDynamicOptions({});
    setLoadingDynamicOptions({});
    setYamlContent('');
    setIsYamlValid(true);
    setDefaultConfigStatus('idle');
    setDefaultConfigLoading(false);
    setDefaultConfigError(null);
    setLoadedForParams('');
  }, [workflow.name]);

  useEffect(() => {
    if (!isOpen || !parameters.tenantCode) return;
    loadOptionsForTenant(parameters.tenantCode as string, parameters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, parameters.tenantCode, workflow.name]);

  // ── Normalise well-known params ──────────────────────────────────────────────

  const normalizeParam = (p: WorkflowParameter): WorkflowParameter => {
    if (p.name === 'provider' && p.type !== 'dynamic-select') {
      return { ...p, type: 'dynamic-select', dynamicSource: 'tenant-providers', dependsOn: ['tenantCode'], required: p.required ?? true, label: p.label || 'Provider' };
    }
    if (p.name === 'environment' && p.type !== 'dynamic-select') {
      return { ...p, type: 'dynamic-select', dynamicSource: 'tenant-environments', dependsOn: ['tenantCode'], required: false, label: p.label || 'Environment (optional)' };
    }
    if (p.name === 'product' && p.type !== 'dynamic-select') {
      return { ...p, type: 'dynamic-select', dynamicSource: 'tenant-devops-products', dependsOn: ['tenantCode'], required: p.required ?? true, label: p.label || 'Product' };
    }
    return p;
  };

  const alwaysHidden = ['maxironYamlB64', 'configOverrides', 'runId', 'triggeredBy'];
  const visibleParams = workflow.parameters
    .filter((p) => !alwaysHidden.includes(p.name))
    .map(normalizeParam);

  // ── Options loading ──────────────────────────────────────────────────────────

  const fetchDynamicOptionsFor = (paramName: string, tenantCode: string, currentParams: Record<string, any>) => {
    const p = visibleParams.find((vp) => vp.name === paramName);
    if (!p || p.type !== 'dynamic-select' || !p.dynamicSource) return;
    setLoadingDynamicOptions((prev) => ({ ...prev, [paramName]: true }));
    const extraParams: Record<string, string> = {};
    if (p.dynamicPath) {
      for (const [k, v] of Object.entries(currentParams)) {
        if (v !== undefined && v !== '') extraParams[k] = String(v);
      }
    }
    apiClient
      .getTenantOptions(tenantCode, p.dynamicSource, p.dynamicPath ? { path: p.dynamicPath, ...extraParams } : {})
      .then((res) => setDynamicOptions((prev) => ({ ...prev, [paramName]: res?.options ?? [] })))
      .catch(() => setDynamicOptions((prev) => ({ ...prev, [paramName]: [] })))
      .finally(() => setLoadingDynamicOptions((prev) => ({ ...prev, [paramName]: false })));
  };

  const loadOptionsForTenant = (tenantCode: string, currentParams: Record<string, any>) => {
    if (!tenantCode) return;
    visibleParams
      .filter((p) => p.type === 'dynamic-select' && p.dynamicSource && p.dependsOn?.includes('tenantCode'))
      .forEach((p) => fetchDynamicOptionsFor(p.name, tenantCode, currentParams));
  };

  const loadTenants = async () => {
    setLoadingTenants(true);
    try {
      const res = await apiClient.getTenants();
      setTenants(res?.tenants || []);
    } catch {
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  // ── Param change ─────────────────────────────────────────────────────────────

  const handleChange = (name: string, value: any) => {
    const next = { ...parameters, [name]: value };
    setParameters(next);

    if (name === 'tenantCode') {
      setDynamicOptions({});
      if (value) loadOptionsForTenant(value as string, next);
    } else {
      const dependentNames = visibleParams
        .filter((p) => p.type === 'dynamic-select' && p.dependsOn?.includes(name))
        .map((p) => p.name);
      const tc = next.tenantCode as string;
      if (tc) dependentNames.forEach((pn) => fetchDynamicOptionsFor(pn, tc, next));
    }

    if (defaultConfigStatus === 'loaded') {
      setDefaultConfigStatus('idle');
      setYamlContent('');
    }
  };

  // ── Load default config ──────────────────────────────────────────────────────

  const requiredParams = visibleParams.filter((p) => p.required !== false);
  const allRequiredFilled = requiredParams.every((p) => {
    const val = parameters[p.name];
    return val !== undefined && val !== null && val !== '';
  });
  const canLoadDefaults = isTenantAware && !!parameters.tenantCode && allRequiredFilled;

  const currentParamsKey = JSON.stringify(
    Object.fromEntries(
      Object.entries(parameters)
        .filter(([k]) => !alwaysHidden.includes(k))
        .sort(([a], [b]) => a.localeCompare(b)),
    ),
  );
  const configIsStale = defaultConfigStatus === 'loaded' && loadedForParams !== currentParamsKey;

  const handleLoadDefaultConfig = async () => {
    setDefaultConfigLoading(true);
    setDefaultConfigError(null);
    try {
      const paramEntries = Object.entries(parameters)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)]);
      const result = await apiClient.getWorkflowDefaultConfig(workflow.name, Object.fromEntries(paramEntries));
      setYamlContent(result.yaml || '');
      setIsYamlValid(true);
      setDefaultConfigStatus('loaded');
      setLoadedForParams(currentParamsKey);
    } catch (err: any) {
      setDefaultConfigError(err?.message || 'Failed to load default configuration');
      setDefaultConfigStatus('error');
    } finally {
      setDefaultConfigLoading(false);
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isConfigurable && !isYamlValid) return;
    const finalCron = usePreset ? PRESETS[preset].cron : cronExpression;
    const finalParameters = isConfigurable
      ? { ...parameters, configOverrides: yamlContent }
      : parameters;
    onSubmit({ workflowName: workflow.name, parameters: finalParameters, schedule: finalCron });
  };

  // ── Param rendering (mirrors WorkflowConfigDialog) ───────────────────────────

  const renderParam = (param: WorkflowParameter) => {
    if (param.name === 'tenantCode') {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || 'Tenant'}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            value={parameters[param.name] || ''}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={param.required !== false}
            disabled={loadingTenants}
          >
            <option value="">{loadingTenants ? 'Loading tenants...' : 'Select a tenant...'}</option>
            {tenants.map((t) => (
              <option key={t.code} value={t.code}>{t.name || t.code}</option>
            ))}
          </select>
        </div>
      );
    }

    if (param.type === 'dynamic-select') {
      const rawChoices = dynamicOptions[param.name] ?? [];
      const disabled = loadingDynamicOptions[param.name] || param.dependsOn?.some((dep) => !parameters[dep]);
      const placeholder = loadingDynamicOptions[param.name]
        ? 'Loading...'
        : param.dependsOn?.some((dep) => !parameters[dep])
          ? `Select ${param.dependsOn?.join(', ')} first`
          : `Select ${param.label || param.name}...`;
      const selectOptions = rawChoices.length > 0 ? rawChoices : (!param.required ? [''] : []);
      const displayLabel = (val: string) => {
        if (val === '') return '(None)';
        return ['provider', 'environment'].includes(param.name) ? val.toUpperCase() : val;
      };

      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={parameters[param.name] ?? ''}
              onChange={(e) => handleChange(param.name, e.target.value)}
              className="hidden md:block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              required={param.required !== false}
              disabled={disabled}
            >
              <option value="">{placeholder}</option>
              {selectOptions.map((opt) => (
                <option key={opt || '__none__'} value={opt}>{displayLabel(opt)}</option>
              ))}
            </select>
            <div className="md:hidden w-full">
              <DropdownMenu
                className="w-full"
                align="left"
                trigger={
                  <button
                    type="button"
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] flex items-center justify-between disabled:opacity-50"
                  >
                    <span className="min-w-0 truncate">
                      {parameters[param.name] !== undefined && parameters[param.name] !== ''
                        ? displayLabel(parameters[param.name])
                        : placeholder}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </button>
                }
                items={[
                  { label: placeholder, icon: !parameters[param.name] ? <Check className="w-4 h-4" /> : undefined, onClick: () => handleChange(param.name, '') },
                  ...selectOptions.map((opt) => ({
                    label: displayLabel(opt),
                    icon: opt === parameters[param.name] ? <Check className="w-4 h-4" /> : undefined,
                    onClick: () => handleChange(param.name, opt),
                  })),
                ]}
              />
            </div>
          </div>
        </div>
      );
    }

    if (param.type === 'boolean') {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
          </label>
          <select
            value={parameters[param.name] !== undefined ? String(parameters[param.name]) : 'false'}
            onChange={(e) => handleChange(param.name, e.target.value === 'true')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="false">False</option>
            <option value="true">True</option>
          </select>
        </div>
      );
    }

    if (param.type === 'choice' && param.choices) {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            value={parameters[param.name] || ''}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
            required={param.required !== false}
          >
            <option value="">-- Select --</option>
            {param.choices.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      );
    }

    if (param.type === 'number') {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          <Input
            type="number"
            value={parameters[param.name] ?? ''}
            onChange={(e) => handleChange(param.name, e.target.value === '' ? undefined : parseFloat(e.target.value))}
            placeholder={param.default !== undefined ? `Default: ${param.default}` : ''}
            required={param.required !== false}
          />
        </div>
      );
    }

    return (
      <div key={param.name} className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {param.label || param.name}
          {param.required !== false && <span className="text-red-500 ml-1">*</span>}
        </label>
        {param.description && <p className="text-xs text-gray-500 dark:text-gray-400">{param.description}</p>}
        <Input
          type="text"
          value={parameters[param.name] || ''}
          onChange={(e) => handleChange(param.name, e.target.value)}
          placeholder={param.default !== undefined ? `Default: ${param.default}` : ''}
          required={param.required !== false}
        />
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent maxWidth="3xl">
        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
          <DialogHeader>
            <DialogTitle>Schedule: {workflow.displayName}</DialogTitle>
          </DialogHeader>

          <DialogBody className="max-h-[75vh]">
            <div className="space-y-5">
              {workflow.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{workflow.description}</p>
              )}

              {/* ── Schedule ─────────────────────────────────────────────────── */}
              <div className="space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Schedule
                </span>
                <div className="flex gap-4">
                  {(['preset', 'custom'] as const).map((mode) => (
                    <label key={mode} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={usePreset === (mode === 'preset')}
                        onChange={() => setUsePreset(mode === 'preset')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {mode === 'preset' ? 'Preset' : 'Custom Cron'}
                      </span>
                    </label>
                  ))}
                </div>

                {usePreset ? (
                  <div className="space-y-1.5">
                    <select
                      value={preset}
                      onChange={(e) => setPreset(e.target.value as PresetKey)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      {Object.entries(PRESETS).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Cron: <code className="font-mono">{PRESETS[preset].cron}</code>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Input
                      type="text"
                      value={cronExpression}
                      onChange={(e) => setCronExpression(e.target.value)}
                      placeholder="0 0 * * *"
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Format: minute hour day month weekday — e.g. <code className="font-mono">0 2 * * 1</code> = every Monday at 02:00
                    </p>
                  </div>
                )}
              </div>

              {/* ── Parameters ───────────────────────────────────────────────── */}
              {visibleParams.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Parameters
                    </span>
                    {requiresDocker && (
                      <span className="text-xs px-2 py-0.5 rounded border border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300">
                        Docker Required
                      </span>
                    )}
                  </div>
                  <div className="space-y-4">
                    {visibleParams.map((param) => renderParam(param))}
                  </div>
                </div>
              )}

              {/* ── Configuration (tenant-aware) ──────────────────────────────── */}
              {isTenantAware && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Configuration
                    </span>
                    {defaultConfigStatus === 'loaded' && !configIsStale && (
                      <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Loaded
                      </span>
                    )}
                    {configIsStale && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">Params changed — reload needed</span>
                    )}
                  </div>

                  {(defaultConfigStatus === 'idle' || configIsStale || defaultConfigStatus === 'error') && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {configIsStale
                              ? 'Parameters changed — reload default configuration'
                              : 'Load the default configuration for this scheduled run'}
                          </p>
                          {!parameters.tenantCode && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Select a tenant above to enable this step.</p>
                          )}
                          {parameters.tenantCode && !allRequiredFilled && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Fill all required parameters to load defaults.</p>
                          )}
                          {defaultConfigStatus === 'error' && defaultConfigError && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{defaultConfigError}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={handleLoadDefaultConfig}
                        disabled={!canLoadDefaults || defaultConfigLoading}
                        className="w-full sm:w-auto gap-2"
                      >
                        {defaultConfigLoading ? (
                          <><RefreshCw className="w-4 h-4 animate-spin" />Loading...</>
                        ) : (
                          <><Download className="w-4 h-4" />{configIsStale ? 'Reload Default Configuration' : 'Load Default Configuration'}</>
                        )}
                      </Button>
                    </div>
                  )}

                  {defaultConfigStatus === 'loaded' && !configIsStale && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Defaults loaded for{' '}
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {parameters.tenantCode}{parameters.provider ? ` / ${parameters.provider}` : ''}
                          </span>. Edit if needed.
                        </p>
                        <button
                          type="button"
                          onClick={handleLoadDefaultConfig}
                          disabled={defaultConfigLoading}
                          className="text-xs flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                        >
                          <RefreshCw className={`w-3 h-3 ${defaultConfigLoading ? 'animate-spin' : ''}`} />
                          Reload
                        </button>
                      </div>
                      <WorkflowConfigEditor
                        value={yamlContent}
                        onChange={(y, v) => { setYamlContent(y); setIsYamlValid(v); }}
                        isLoading={defaultConfigLoading}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ── Manual configurable (non-tenant-aware) ──────────────────── */}
              {isConfigurable && !isTenantAware && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-5 space-y-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    Configuration (YAML)
                  </span>
                  <WorkflowConfigEditor
                    value={yamlContent || '# Add your workflow configuration here\n'}
                    onChange={(y, v) => { setYamlContent(y); setIsYamlValid(v); }}
                  />
                </div>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                (isConfigurable && !isYamlValid) ||
                (isTenantAware && defaultConfigStatus !== 'loaded')
              }
            >
              <Plus className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Creating...' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
